import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import {
  WEEKDAYS, DAY_TITLES, cellText, num, amount, rateLabel, unitOf, closedNote,
} from './tsFormat';

/* Табель на месяц — замена Excel-файла бани.
   Строка — сотрудник на должности, колонка — число месяца. Между 15 и 16
   числом проходит граница выплат: всё, что левее, — первая выплата, правее —
   вторая (так устроен Excel-табель: «аванс 1» и «аванс 2»).

   Ввод — как в таблице: стрелки, Enter, Tab, набор поверх клетки, Delete,
   выделение мышью или Shift+стрелками и заливка выделенного одним значением,
   Ctrl+C / Ctrl+V блоком прямо из Excel. Клетки — обычные td, поле ввода
   одно и появляется только в активной клетке: 45 строк × 31 день — это
   1400 клеток, и 1400 input-ов делали бы прокрутку вязкой.

   Ничего не считается здесь: часы, половины и итог возвращает сервер, чтобы
   две арифметики не разошлись. */



const AmountCell = memo(({ value, disabled, onSave, manual, onReset, title, className = '' }) => {
  const [draft, setDraft] = useState(null);
  const dirty = useRef(false);

  const commit = async () => {
    const was = dirty.current;
    dirty.current = false;
    const text = draft;
    setDraft(null);
    if (!was || text === null) return;
    const next = Number(String(text).replace(/\s/g, '').replace(',', '.'));
    if (Number.isNaN(next) || next < 0 || next === Number(value)) return;
    await onSave(next);
  };

  if (draft !== null) {
    return (
      <td className={`ts-num ts-editing ${className}`}>
        <input
          className="ts-amount-input"
          autoFocus
          value={draft}
          inputMode="decimal"
          autoComplete="off"
          onChange={(e) => { dirty.current = true; setDraft(e.target.value); }}
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') { dirty.current = false; setDraft(null); }
          }}
          onFocus={(e) => e.target.select()}
        />
      </td>
    );
  }
  return (
    <td className={`ts-num ${disabled ? '' : 'ts-editable'} ${className}`} title={title}>
      <button
        type="button"
        className="ts-amount"
        disabled={disabled}
        onClick={() => setDraft(String(value || ''))}
      >
        {amount(value)}
      </button>
      {manual && (
        <button
          type="button"
          className="ts-manual"
          disabled={disabled}
          title="Сумма введена вручную. Нажмите, чтобы вернуть расчёт по дням"
          aria-label="Вернуть расчёт по дням"
          onClick={onReset}
        />
      )}
    </td>
  );
});

const Row = memo(({
  entry, r, cells, dayMeta, sel, activeC, draft, disabled,
  onDraft, onEditKey, onOpenEntry, onSaveEntry, onResetHalf,
}) => {
  const save = (field) => (value) => onSaveEntry(entry.id, field, value);
  return (
    <tr className={`ts-row ${entry.employee_status === 'fired' ? 'is-fired' : ''}`}>
      <th scope="row" className="ts-name">
        <button type="button" className="ts-name-btn" onClick={() => onOpenEntry(entry)}>
          <span className="ts-person">{entry.full_name}</span>
          <span className="ts-rate">{rateLabel(entry.pay_type, entry.rate)}</span>
        </button>
      </th>
      {dayMeta.map(({ day, cls }) => {
        const cell = cells && cells[day];
        const type = cell ? cell.day_type : 'work';
        const inSel = sel && day >= sel.c1 && day <= sel.c2;
        const isActive = activeC === day;
        const className = `ts-c ${cls} t-${type}${inSel ? ' is-sel' : ''}${isActive ? ' is-active' : ''}`;
        if (isActive && draft !== null) {
          return (
            <td key={day} data-r={r} data-c={day} className={`${className} is-editing`}>
              <input
                className="ts-cell-input"
                autoFocus
                value={draft}
                autoComplete="off"
                spellCheck={false}
                aria-label={`${entry.full_name}, ${day} число`}
                onChange={(e) => onDraft(e.target.value)}
                onKeyDown={onEditKey}
              />
            </td>
          );
        }
        return (
          <td key={day} data-r={r} data-c={day} className={className}
            title={type !== 'work' ? (cell.mark ? `${DAY_TITLES[type]}: ${cell.mark}` : DAY_TITLES[type]) : undefined}>
            {cellText(cell)}
          </td>
        );
      })}
      <td className="ts-num ts-sum-first">
        {num(entry.hours)} <span className="ts-unit">{unitOf(entry.pay_type, entry.hours)}</span>
      </td>
      <AmountCell value={entry.rate} disabled={disabled} onSave={save('rate')}
        title={entry.pay_type === 'salary' ? 'Оклад в месяц' : entry.pay_type === 'shift' ? 'За смену' : 'За час'} />
      <AmountCell value={entry.advance1} disabled={disabled} onSave={save('advance1')}
        manual={entry.adv1_manual} onReset={() => onResetHalf(entry.id, 1)} />
      <AmountCell value={entry.advance2} disabled={disabled} onSave={save('advance2')}
        manual={entry.adv2_manual} onReset={() => onResetHalf(entry.id, 2)} />
      <AmountCell value={entry.bonus} disabled={disabled} onSave={save('bonus')} />
      <AmountCell value={entry.fines} disabled={disabled} onSave={save('fines')} className="ts-fines" />
      <td className="ts-num ts-total">{amount(entry.total)}</td>
    </tr>
  );
});

const TimesheetGrid = ({
  period, entries, days, daysInMonth, closed, todayDay, filter,
  onSaveCells, onSaveEntry, onResetHalf, onOpenEntry, onClosedDay,
}) => {
  const n = daysInMonth;
  const wrapRef = useRef(null);
  const [active, setActive] = useState(null);      // {r, c}
  const [anchor, setAnchor] = useState(null);
  const [draft, setDraftState] = useState(null);   // строка в поле ввода или null
  /* Черновик дублируется в ref: быстрый набор («11» + Enter) приходит
     быстрее, чем React перерисует поле и отдаст ему фокус, и обработчики
     читали бы устаревшее значение из замыкания — вторая цифра терялась. */
  const draftRef = useRef(null);
  const setDraft = useCallback((value) => {
    const next = typeof value === 'function' ? value(draftRef.current) : value;
    draftRef.current = next;
    setDraftState(next);
  }, []);
  const [dayMenu, setDayMenu] = useState(null);    // {day, note, closed}
  const dragging = useRef(false);
  const keyboardMove = useRef(false);

  /* Видимые строки: поиск и фильтр по должности. Группы — подряд идущие
     строки одной должности (сервер уже отсортировал их в порядке табеля). */
  const visible = useMemo(() => {
    const q = (filter.search || '').trim().toLowerCase();
    return entries.filter(e => (!q || e.full_name.toLowerCase().includes(q))
      && (filter.group === 'all' || e.position === filter.group));
  }, [entries, filter.search, filter.group]);

  const sections = useMemo(() => {
    const out = [];
    visible.forEach((entry, r) => {
      const last = out[out.length - 1];
      if (!last || last.position !== entry.position) {
        out.push({ position: entry.position, rows: [] });
      }
      out[out.length - 1].rows.push({ entry, r });
    });
    return out.map(s => ({
      ...s,
      people: s.rows.length,
      half1: s.rows.reduce((a, { entry }) => a + entry.advance1, 0),
      half2: s.rows.reduce((a, { entry }) => a + entry.advance2, 0),
      bonus: s.rows.reduce((a, { entry }) => a + entry.bonus, 0),
      fines: s.rows.reduce((a, { entry }) => a + entry.fines, 0),
      total: s.rows.reduce((a, { entry }) => a + entry.total, 0),
    }));
  }, [visible]);

  const dayMeta = useMemo(() => {
    const list = [];
    for (let day = 1; day <= n; day += 1) {
      const weekday = new Date(period.year, period.month - 1, day).getDay();
      const weekend = weekday === 0 || weekday === 6;
      const isClosed = closedNote(period, day) !== null;
      const cls = [
        weekend ? 'is-weekend' : '',
        isClosed ? 'is-closed-day' : '',
        day === 16 ? 'is-seam' : '',
        day === todayDay ? 'is-today' : '',
      ].filter(Boolean).join(' ');
      list.push({ day, weekday, weekend, isClosed, cls });
    }
    return list;
  }, [n, period, todayDay]);

  /* Сколько людей вышло в каждый день — видно недобор смены. */
  const onShift = useMemo(() => {
    const counts = {};
    visible.forEach(e => {
      const byDay = days[e.id] || {};
      Object.entries(byDay).forEach(([day, cell]) => {
        if (cell.day_type === 'work' && cell.hours > 0) counts[day] = (counts[day] || 0) + 1;
      });
    });
    return counts;
  }, [visible, days]);

  const totals = useMemo(() => visible.reduce((acc, e) => {
    if (e.pay_type === 'hourly') acc.hours += e.hours;
    acc.half1 += e.advance1; acc.half2 += e.advance2;
    acc.bonus += e.bonus; acc.fines += e.fines; acc.total += e.total;
    return acc;
  }, { hours: 0, half1: 0, half2: 0, bonus: 0, fines: 0, total: 0 }), [visible]);

  const rowsCount = visible.length;
  const sel = active && anchor ? {
    r1: Math.min(active.r, anchor.r), r2: Math.max(active.r, anchor.r),
    c1: Math.min(active.c, anchor.c), c2: Math.max(active.c, anchor.c),
  } : null;
  const selCount = sel ? (sel.r2 - sel.r1 + 1) * (sel.c2 - sel.c1 + 1) : 0;

  // При смене месяца или фильтра выделение больше ничему не соответствует.
  useEffect(() => { setActive(null); setAnchor(null); setDraft(null); }, [period.id, filter.search, filter.group, setDraft]);

  // Клетку, к которой перешли с клавиатуры, держим в поле зрения.
  useEffect(() => {
    if (!active || !keyboardMove.current) return;
    keyboardMove.current = false;
    const td = wrapRef.current?.querySelector(`td[data-r="${active.r}"][data-c="${active.c}"]`);
    if (td) td.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [active]);

  useEffect(() => {
    const up = () => { dragging.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  /* Фокус сетки держит невидимое поле: так набор работает и там, где
     браузер отдаёт текст без обычного keydown (метод ввода, автоматизация),
     — текст из поля сразу превращается в ввод активной клетки. */
  const trapRef = useRef(null);
  const focusGrid = () => (trapRef.current || wrapRef.current)?.focus({ preventScroll: true });
  const onTrapInput = (e) => {
    const text = e.target.value;
    e.target.value = '';
    if (!text || closed || !active) return;
    if (draftRef.current !== null) setDraft(d => (d || '') + text);
    else setDraft(text);
  };

  const move = (dr, dc, extend = false) => {
    if (!rowsCount) return;
    const from = active || { r: 0, c: 1 };
    const next = {
      r: Math.max(0, Math.min(rowsCount - 1, from.r + dr)),
      c: Math.max(1, Math.min(n, from.c + dc)),
    };
    keyboardMove.current = true;
    setActive(next);
    if (!extend) setAnchor(next);
  };

  const cellsOfSelection = () => {
    if (!sel) return [];
    const list = [];
    for (let r = sel.r1; r <= sel.r2; r += 1) {
      for (let c = sel.c1; c <= sel.c2; c += 1) list.push({ r, c });
    }
    return list;
  };

  const save = useCallback((items) => {
    if (closed || !items.length) return;
    onSaveCells(items);
  }, [closed, onSaveCells]);

  /* Набранное значение уходит во ВСЕ выделенные клетки — так проставляют
     «в» на неделю или 12 часов на полмесяца одним движением. */
  const commit = (value, after) => {
    setDraft(null);
    if (value === null || value === undefined) return;
    const targets = selCount > 1 ? cellsOfSelection() : (active ? [active] : []);
    const text = String(value ?? '').trim();
    // Клетки, где уже стоит то же самое, не отправляем — в истории правок
    // не должно появляться «12 → 12».
    save(targets
      .filter(({ r }) => visible[r])
      .map(({ r, c }) => ({ entry_id: visible[r].id, day: c, value: text }))
      .filter(item => cellText(days[item.entry_id]?.[item.day]) !== text.replace('.', ',')
        && cellText(days[item.entry_id]?.[item.day]) !== text));
    focusGrid();
    if (after) after();
  };

  const startEdit = (initial) => {
    if (closed || !active) return;
    setDraft(initial);
  };

  const onEditKey = (e) => {
    e.stopPropagation();
    const value = draftRef.current;
    if (e.key === 'Enter') { e.preventDefault(); commit(value, () => move(e.shiftKey ? -1 : 1, 0)); }
    else if (e.key === 'Tab') { e.preventDefault(); commit(value, () => move(0, e.shiftKey ? -1 : 1)); }
    else if (e.key === 'Escape') { e.preventDefault(); setDraft(null); focusGrid(); }
    else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      commit(value, () => move(e.key === 'ArrowUp' ? -1 : 1, 0));
    }
  };

  const copySelection = (e) => {
    if (!sel || !fromGrid(e)) return;
    const lines = [];
    for (let r = sel.r1; r <= sel.r2; r += 1) {
      const row = [];
      for (let c = sel.c1; c <= sel.c2; c += 1) row.push(cellText(days[visible[r]?.id]?.[c]));
      lines.push(row.join('\t'));
    }
    e.clipboardData.setData('text/plain', lines.join('\n'));
    e.preventDefault();
  };

  /* Вставка блока из Excel: строки — сотрудники сверху вниз от активной
     клетки, колонки — дни вправо. Одно значение при выделенном диапазоне
     заливает весь диапазон. */
  const onPaste = (e) => {
    if (closed || !active || draftRef.current !== null || !fromGrid(e)) return;
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    e.preventDefault();
    const grid = text.replace(/\r/g, '').replace(/\n$/, '').split('\n').map(line => line.split('\t'));
    if (grid.length === 1 && grid[0].length === 1 && selCount > 1) {
      commit(grid[0][0]);
      return;
    }
    const start = sel ? { r: sel.r1, c: sel.c1 } : active;
    const items = [];
    grid.forEach((cols, i) => {
      const entry = visible[start.r + i];
      if (!entry) return;
      cols.forEach((value, j) => {
        const day = start.c + j;
        if (day <= n) items.push({ entry_id: entry.id, day, value: value.trim() });
      });
    });
    save(items);
    const end = { r: Math.min(rowsCount - 1, start.r + grid.length - 1), c: Math.min(n, start.c + grid[0].length - 1) };
    setAnchor(start);
    setActive(end);
  };

  // Клавиши из меню дня и прочих полей внутри таблицы — не наши.
  const fromGrid = (e) => e.target === wrapRef.current || e.target === trapRef.current;

  const onKeyDown = (e) => {
    if (!rowsCount || !fromGrid(e)) return;
    if (draftRef.current !== null) {
      // Поле ещё не успело получить фокус — набранное дописываем сами.
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setDraft(d => (d || '') + e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setDraft(d => (d || '').slice(0, -1));
      } else {
        onEditKey(e);
      }
      return;
    }
    const ctrl = e.ctrlKey || e.metaKey;
    const arrows = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
    if (arrows[e.key]) {
      e.preventDefault();
      if (!active) { move(0, 0); return; }
      const [dr, dc] = arrows[e.key];
      if (ctrl) move(dr * 100, dc * 100, e.shiftKey);
      else move(dr, dc, e.shiftKey);
      return;
    }
    if (!active) return;
    if (e.key === 'Tab') { e.preventDefault(); move(0, e.shiftKey ? -1 : 1); return; }
    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      startEdit(selCount > 1 ? '' : cellText(days[visible[active.r]?.id]?.[active.c]));
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); commit(''); return; }
    if (e.key === 'Escape') { setAnchor(active); return; }
    if (ctrl && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      setAnchor({ r: 0, c: 1 });
      setActive({ r: rowsCount - 1, c: n });
      return;
    }
    if (!ctrl && !e.altKey && e.key.length === 1) {
      e.preventDefault();
      startEdit(e.key);
    }
  };

  const cellFromEvent = (e) => {
    const td = e.target.closest('td[data-c]');
    if (!td) return null;
    return { r: Number(td.dataset.r), c: Number(td.dataset.c) };
  };

  const onMouseDown = (e) => {
    const cell = cellFromEvent(e);
    if (!cell || e.button !== 0) return;
    if (draftRef.current !== null) {
      if (active && cell.r === active.r && cell.c === active.c) return;
      commit(draftRef.current);
    }
    e.preventDefault();
    focusGrid();
    setActive(cell);
    if (!e.shiftKey || !anchor) setAnchor(cell);
    dragging.current = true;
  };

  const onMouseOver = (e) => {
    if (!dragging.current) return;
    const cell = cellFromEvent(e);
    if (cell && (!active || cell.r !== active.r || cell.c !== active.c)) setActive(cell);
  };

  const onDoubleClick = (e) => {
    const cell = cellFromEvent(e);
    if (!cell) return;
    setActive(cell);
    setAnchor(cell);
    if (!closed) setDraft(cellText(days[visible[cell.r]?.id]?.[cell.c]));
  };

  const openDayMenu = (day) => {
    if (closed) return;
    const note = closedNote(period, day);
    setDayMenu({ day, note: note || '', closed: note !== null });
  };

  const half2Label = `16–${n}`;
  const colSpanAll = n + 8;

  return (
    <div className="ts-grid-shell">
      <div
        ref={wrapRef}
        className={`ts-wrap ${closed ? 'is-locked' : ''}`}
        tabIndex={0}
        role="grid"
        aria-label={`Табель, ${rowsCount} строк`}
        aria-readonly={closed}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onCopy={copySelection}
      >
        <input
          ref={trapRef}
          className="ts-keytrap"
          aria-hidden="true"
          tabIndex={-1}
          autoComplete="off"
          onInput={onTrapInput}
        />
        <table className="ts-table">
          <colgroup>
            <col className="ts-col-name" />
            {dayMeta.map(({ day }) => <col key={day} className="ts-col-day" />)}
            <col className="ts-col-hours" />
            <col className="ts-col-rate" />
            <col className="ts-col-money" />
            <col className="ts-col-money" />
            <col className="ts-col-small" />
            <col className="ts-col-small" />
            <col className="ts-col-total" />
          </colgroup>
          <thead>
            <tr className="ts-head-halves">
              <th rowSpan={2} className="ts-name ts-corner" scope="col">
                <span>Сотрудник</span>
              </th>
              <th colSpan={15} className="ts-half">Выплата за 1–15</th>
              <th colSpan={n - 15} className="ts-half is-seam">Выплата за {half2Label}</th>
              <th rowSpan={2} className="ts-num-h ts-sum-first">Часы</th>
              <th rowSpan={2} className="ts-num-h">Ставка, ₽</th>
              <th rowSpan={2} className="ts-num-h">За 1–15, ₽</th>
              <th rowSpan={2} className="ts-num-h">За {half2Label}, ₽</th>
              <th rowSpan={2} className="ts-num-h">Доплаты</th>
              <th rowSpan={2} className="ts-num-h">Штрафы</th>
              <th rowSpan={2} className="ts-num-h ts-total">Итого, ₽</th>
            </tr>
            <tr className="ts-head-days">
              {dayMeta.map(({ day, weekday, cls, isClosed }) => (
                <th key={day} scope="col" className={`ts-day ${cls}`}>
                  <button
                    type="button"
                    className="ts-day-btn"
                    disabled={closed}
                    onClick={() => openDayMenu(day)}
                    title={isClosed ? `Не работали: ${closedNote(period, day) || ''}` : 'Отметить день, когда не работали все'}
                  >
                    <span className="ts-day-num">{day}</span>
                    <span className="ts-day-wd">{WEEKDAYS[weekday]}</span>
                  </button>
                  {dayMenu && dayMenu.day === day && (
                    <DayMenu
                      period={period}
                      menu={dayMenu}
                      alignRight={day > n - 6}
                      onClose={() => setDayMenu(null)}
                      onSave={(closedFlag, note) => { onClosedDay(day, note, closedFlag); setDayMenu(null); }}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody onMouseDown={onMouseDown} onMouseOver={onMouseOver} onDoubleClick={onDoubleClick}>
            {rowsCount === 0 ? (
              <tr>
                <td colSpan={colSpanAll} className="ts-empty">
                  {entries.length ? 'Никто не подходит под поиск.' : 'В этом месяце пока нет ни одной строки — добавьте сотрудников.'}
                </td>
              </tr>
            ) : sections.map(section => (
              <React.Fragment key={section.position}>
                <tr className="ts-group">
                  <th scope="rowgroup" className="ts-name ts-group-name">
                    <span className="ts-group-title">{section.position || 'Без должности'}</span>
                    <span className="ts-group-count">{section.people}</span>
                  </th>
                  <td colSpan={n} className="ts-group-fill" />
                  <td className="ts-sum-first" />
                  <td />
                  <td className="ts-num">{amount(section.half1)}</td>
                  <td className="ts-num">{amount(section.half2)}</td>
                  <td className="ts-num">{amount(section.bonus)}</td>
                  <td className="ts-num ts-fines">{amount(section.fines)}</td>
                  <td className="ts-num ts-total">{amount(section.total)}</td>
                </tr>
                {section.rows.map(({ entry, r }) => {
                  const inRows = sel && r >= sel.r1 && r <= sel.r2;
                  const isActiveRow = active && active.r === r;
                  return (
                    <Row
                      key={entry.id}
                      entry={entry}
                      r={r}
                      cells={days[entry.id]}
                      dayMeta={dayMeta}
                      sel={inRows ? sel : null}
                      activeC={isActiveRow ? active.c : null}
                      draft={isActiveRow ? draft : null}
                      disabled={closed}
                      onDraft={setDraft}
                      onEditKey={onEditKey}
                      onOpenEntry={onOpenEntry}
                      onSaveEntry={onSaveEntry}
                      onResetHalf={onResetHalf}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>

          {rowsCount > 0 && (
            <tfoot>
              <tr>
                <th scope="row" className="ts-name ts-foot-name">
                  <span>Вышли на смену</span>
                  <span className="ts-foot-hint">человек</span>
                </th>
                {dayMeta.map(({ day, cls }) => (
                  <td key={day} className={`ts-c ts-foot-day ${cls}`}>{onShift[day] || ''}</td>
                ))}
                <td className="ts-num ts-sum-first" title="Часы почасовых сотрудников">{num(totals.hours)}</td>
                <td />
                <td className="ts-num">{amount(totals.half1)}</td>
                <td className="ts-num">{amount(totals.half2)}</td>
                <td className="ts-num">{amount(totals.bonus)}</td>
                <td className="ts-num ts-fines">{amount(totals.fines)}</td>
                <td className="ts-num ts-total">{amount(totals.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {selCount > 1 && !closed && (
        <div className="ts-selbar" role="status">
          Выделено {selCount} {selCount % 10 === 1 && selCount % 100 !== 11 ? 'клетка' : 'клеток'}.
          Наберите значение и нажмите Enter — оно встанет во все. Delete очищает.
        </div>
      )}
    </div>
  );
};

const DayMenu = ({ period, menu, alignRight, onClose, onSave }) => {
  const [note, setNote] = useState(menu.note);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const date = new Date(period.year, period.month - 1, menu.day)
    .toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'long' });

  return (
    <div ref={ref} className={`ts-daymenu ${alignRight ? 'is-right' : ''}`} role="dialog" aria-label={date}>
      <div className="ts-daymenu-title">{date}</div>
      <p className="ts-daymenu-text">
        {menu.closed
          ? 'Отмечено: в этот день не работала вся организация.'
          : 'Если в этот день не работали все (отключили воду, газ), отметьте его — колонка будет заштрихована. Оклад за такой день не уменьшается.'}
      </p>
      <input
        className="ts-daymenu-input"
        value={note}
        placeholder="Причина, например «отключили воду»"
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') onSave(true, note); }}
        autoFocus
      />
      <div className="ts-daymenu-actions">
        {menu.closed && (
          <button type="button" className="ui-btn-ghost" onClick={() => onSave(false, '')}>Снять отметку</button>
        )}
        <button type="button" className="ui-btn-primary" onClick={() => onSave(true, note)}>
          {menu.closed ? 'Сохранить' : 'Не работали'}
        </button>
      </div>
    </div>
  );
};

export default TimesheetGrid;
